import { useEffect, useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { CopySimple, DownloadSimple, ShareNetwork, X } from "@phosphor-icons/react";

/** Generic shareable-card modal — fetches {card_url, short_url} from metaUrl
 * and shows the branded PNG with copy + download actions. Used by observation
 * cards and catalogue entity cards. */
export default function ShareCardModal({
  metaUrl,
  open,
  onClose,
  imageAlt = "Shareable card",
  downloadName = "naturepulse-card.png",
  note = "Print it, post it, or scan the QR — anyone with the link lands on this page.",
  testidPrefix = "share-card",
}) {
  const [meta, setMeta] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMeta(null);
    http.get(metaUrl)
      .then(({ data }) => setMeta(data))
      .catch(() => { toast.error("Couldn't prepare the share card"); onClose(); });
  }, [open, metaUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const backend = process.env.REACT_APP_BACKEND_URL || "";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(meta?.short_url || "");
      setCopied(true);
      toast.success("Short link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — long-press to copy manually");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid={`${testidPrefix}-modal`}
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-2xl bg-[color:var(--np-bg,#F5EBDC)] border border-black/10 p-4 md:p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#1B4332]">Shareable card</div>
          <button onClick={onClose} data-testid={`${testidPrefix}-close`}
            className="rounded-full border border-black/15 p-1.5 hover:bg-black/5" aria-label="Close">
            <X size={14} weight="bold" />
          </button>
        </div>

        {!meta ? (
          <div className="py-16 text-center font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#3A3830]/70" data-testid={`${testidPrefix}-loading`}>
            Painting your card…
          </div>
        ) : (
          <>
            <img
              src={`${backend}${meta.card_url}`}
              alt={imageAlt}
              data-testid={`${testidPrefix}-image`}
              className="w-full rounded-xl border border-black/10"
            />
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <code className="flex-1 min-w-0 truncate rounded-lg border border-black/10 bg-white px-3 py-2 text-xs" data-testid={`${testidPrefix}-shortlink`}>
                {meta.short_url}
              </code>
              <button onClick={copy} data-testid={`${testidPrefix}-copy`}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase">
                <CopySimple size={12} weight="bold" /> {copied ? "Copied" : "Copy link"}
              </button>
              <a href={`${backend}${meta.card_url}`} download={downloadName} target="_blank" rel="noreferrer"
                data-testid={`${testidPrefix}-download`}
                className="inline-flex items-center gap-1.5 rounded-full border border-black/15 px-4 py-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-black/5">
                <DownloadSimple size={12} weight="bold" /> PNG
              </a>
            </div>
            <p className="mt-2 text-[11px] text-[#3A3830]/70">{note}</p>
          </>
        )}
      </div>
    </div>
  );
}

/** Trigger button + modal bundle — drop-in "Share card" chip for any page
 * with a card endpoint. */
export function ShareCardButton({ metaUrl, imageAlt, downloadName, note, testid = "share-card-btn", testidPrefix = "share-card" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid={testid}
        className="inline-flex items-center gap-1.5 rounded-full border border-[#1B4332]/30 bg-[#1B4332]/10 text-[#1B4332] px-4 py-2 font-mono-tech text-[10px] tracking-[0.22em] uppercase hover:bg-[#1B4332] hover:text-[#F5EBDC] transition-colors"
      >
        <ShareNetwork size={13} weight="bold" /> Share card
      </button>
      <ShareCardModal
        metaUrl={metaUrl}
        open={open}
        onClose={() => setOpen(false)}
        imageAlt={imageAlt}
        downloadName={downloadName}
        note={note}
        testidPrefix={testidPrefix}
      />
    </>
  );
}
